/**
 * Product identity, in one place.
 *
 * Every user-visible occurrence of the product name comes from here, so
 * renaming the product is a change to this file rather than a search across the
 * codebase. That matters more than usual for this project: it began as
 * `gdi-labour`, named after the anchor customer, and shipped that name into a
 * browser title, three authentication screens, an invitation email and an
 * export filename before anyone noticed.
 *
 * **Repository, database and deployment identifiers are deliberately not
 * here.** The Cloudflare project is still `gdi-labour-sandbox`, the git
 * repository is still `gdi-labour`, and the migrations still say what they said
 * when they were applied. Renaming any of those to match a wordmark would be
 * churn with a real chance of breaking a deployment, and none of them is
 * visible to a customer.
 *
 * **No logo asset exists yet.** This is a wordmark treatment, not a claim to be
 * the final registered identity. When a logo arrives it replaces `Wordmark` in
 * `src/ui/patterns/AppShell.tsx` and nothing else has to move.
 */

/** The product family. Future modules may join it, such as Swamito Toolbox. */
export const BRAND_FAMILY = 'Swamito'

/** This module. */
export const BRAND_MODULE = 'Labour Intelligence'

/** The full product name, for a browser title or a first mention. */
export const BRAND_PRODUCT = `${BRAND_FAMILY} ${BRAND_MODULE}`

/**
 * What the product does, in one line a business reader recognises.
 *
 * Deliberately descriptive rather than promotional. It claims labour hours,
 * cost and reporting, all of which the product demonstrably does, and claims
 * nothing about payroll, compliance or accuracy against a client's own figures,
 * none of which has been validated.
 */
export const BRAND_TAGLINE = 'Labour visibility, costing and reporting in one place'

/** A shorter form for the application shell, where space is tight. */
export const BRAND_SHELL_TAGLINE = 'Labour, jobs and cost'

/**
 * How each environment names itself.
 *
 * Production is null because a finished product should not tell a customer
 * which server they are on. Sandbox and local say so plainly, because
 * mistaking one for the other is the expensive mistake.
 */
export const ENVIRONMENT_LABELS: Readonly<Record<string, string | null>> = {
  local: 'Local',
  sandbox: 'Sandbox',
  production: null,
}

export function environmentLabel(environment: string): string | null {
  return ENVIRONMENT_LABELS[environment] ?? null
}

/**
 * The browser title for a screen.
 *
 * `Jobs | Swamito Labour Intelligence`, or just the product name on a screen
 * that has no title of its own.
 */
export function documentTitle(pageTitle?: string): string {
  return pageTitle === undefined || pageTitle === '' ? BRAND_PRODUCT : `${pageTitle} | ${BRAND_PRODUCT}`
}

/** The export filename prefix. Slugged, lowercase, no spaces. */
export const BRAND_FILE_PREFIX = 'swamito-labour'
